import { Observable, ReplaySubject } from 'rxjs';
import { tap } from 'rxjs/operators';
import { DrServerEvent } from '@app/shared/models/server-event.model';
import { Injectable, NgZone } from '@angular/core';
import { AuthenticationService } from '../authentication';
import { environment } from '../../../environments/environment';

@Injectable({
    providedIn: 'root',
})
export class ServerEventService<T> {

    private _subject: ReplaySubject<DrServerEvent<T>> = new ReplaySubject(1);

    private _eventSource?: EventSource;

    private _reconnectTimeout?: any;
    private _reconnectIntervalSec: number = 1;

    constructor(private _zone: NgZone, protected authService: AuthenticationService) {}

    get events(): Observable<DrServerEvent<T>> {
        if (!this._eventSource) {
            this.connect();
        }

        return this._subject
            .asObservable()
            .pipe(tap((event) => console.log('received serverEvent:', event.event)));
    }

    private createEventSource(url: string): EventSource {
        return new EventSource(url);
    }

    private closeEventSource() {
        this._eventSource?.close();
        this._eventSource = undefined;
    }

    private processEvent(event: MessageEvent) {
        const data: {
            receiver: string;
            group: string;
            event: DrServerEvent<T>;
        } = event.data ? JSON.parse(event.data) : {};

        return data.event;
    }

    private reconnectOnError() {
        this.closeEventSource();

        clearTimeout(this._reconnectTimeout);

        this._reconnectTimeout = setTimeout(() => {
            this._reconnectIntervalSec *= 2;

            if (this._reconnectIntervalSec > 10) {
                this._reconnectIntervalSec = 10;
            }

            this.connect();
        }, this._reconnectIntervalSec * 1000);
    }

    connect() {
        this.disconnect();

        const url = environment.apiUrl;
        const accessToken = this.authService.currentUserValue.accessToken;

        if (!accessToken) {
            return;
        }

        this._eventSource = this.createEventSource(
            `${url}/eventpusher/sse?access-token=${encodeURIComponent(accessToken)}`
        );

        this._eventSource.addEventListener(
            'message',
            (event: MessageEvent<string>) => {
                this._zone.run(() => {
                    this._subject.next(this.processEvent(event));
                });
            }
        );

        this._eventSource.onopen = () => {
            this._reconnectIntervalSec = 1;
        };

        this._eventSource.onerror = (error) => {
            console.log('EventSource error', error);
            this.reconnectOnError();
        };
    }

    disconnect() {
        this.closeEventSource();
    }
}
